import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const plans = [
  {
    name: "Customer",
    eyebrow: "For households &amp; businesses",
    price: "Free",
    period: "to post",
    note: "Pay only when a quote is accepted — 5% service fee, no monthly cost.",
    features: [
      "Unlimited job posts",
      "Stripe escrow on every job",
      "Verified Luton van driver pool",
      "Dispute mediation by Raanah",
      "Exportable VAT-friendly receipts",
    ],
    cta: "Book a move",
    accent: false,
  },
  {
    name: "Driver Pro",
    eyebrow: "For self-employed Luton van drivers",
    price: "£29",
    period: "/ month",
    note: "Free tier available — pay 12% commission per job.",
    features: [
      "Priority job alerts on WhatsApp",
      "Lower 6% commission per job",
      "Same-day payout option",
      "Verified & insured badge on profile",
      "MOT & doc expiry concierge",
    ],
    cta: "Apply &amp; subscribe",
    accent: true,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-white"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="max-w-2xl mb-14">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
            Memberships
          </p>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
            Honest pricing.
            <br />
            No surprise deductions.
          </h2>
          <p className="mt-5 text-[#475569] text-lg">
            Pay as you go, or subscribe to lower commission and unlock priority
            access. All plans include escrow, ratings, and WhatsApp alerts.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-w-5xl border border-[#0A0A0A]/10">
          {plans.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
              data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`p-8 md:p-10 ${
                p.accent
                  ? "bg-[#0A0A0A] text-white"
                  : "bg-white text-[#0A0A0A]"
              } ${i === 0 ? "md:border-r border-[#0A0A0A]/10" : ""}`}
            >
              <p
                className={`text-[11px] uppercase tracking-[0.22em] font-bold ${
                  p.accent ? "text-[#5BB247]" : "text-[#1E4D55]"
                }`}
                dangerouslySetInnerHTML={{ __html: p.eyebrow }}
              />
              <h3 className="font-display mt-3 text-3xl font-black tracking-tight">
                {p.name}
              </h3>

              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-display text-5xl font-black tracking-tighter">
                  {p.price}
                </span>
                <span
                  className={`text-sm ${
                    p.accent ? "text-white/60" : "text-[#475569]"
                  }`}
                >
                  {p.period}
                </span>
              </div>
              <p
                className={`mt-2 text-sm ${
                  p.accent ? "text-white/70" : "text-[#475569]"
                }`}
              >
                {p.note}
              </p>

              <ul className="mt-8 space-y-3">
                {p.features.map((f, idx) => (
                  <li
                    key={idx}
                    className={`flex items-start gap-3 text-sm ${
                      p.accent ? "text-white/90" : "text-[#0A0A0A]"
                    }`}
                  >
                    <Check
                      size={16}
                      className={`mt-0.5 ${
                        p.accent ? "text-[#5BB247]" : "text-[#1E4D55]"
                      }`}
                      strokeWidth={3}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#waitlist"
                data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`mt-10 group inline-flex items-center justify-between gap-2 px-6 py-4 font-semibold transition-colors w-full ${
                  p.accent
                    ? "bg-white text-[#0A0A0A] hover:bg-[#5BB247] hover:text-white"
                    : "bg-[#1E4D55] text-white hover:bg-[#163C42]"
                }`}
              >
                <span dangerouslySetInnerHTML={{ __html: p.cta }} />
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-sm text-[#475569]">
          * Pricing is indicative for pilot launch. Final tiers may adjust before
          public rollout.
        </p>
      </div>
    </section>
  );
}
